// ─────────────────────────────────────────────────────────────────────────────
// Automation Insights — Dummy Data (local preview only)
//
// Mirrors the standalone repo's lib/dummy-data.ts / lib/mock-panel-data.ts +
// USE_DUMMY_DATA pattern, so you can preview the look of all three panels
// locally without the Azure AD consent, CORS, or the old backend being
// reachable at all — just sign into this platform as normal.
//
// Turn on: add REACT_APP_AUTOMATION_USE_DUMMY_DATA=true to your local .env
// (already git-ignored in this repo), then `npm start`. Turn off (or just
// delete the line) to go back to real data once Phase 1 auth lands. Never
// set this in a deployed environment — it's a local dev-only escape hatch,
// not a feature toggle for the built app.
// ─────────────────────────────────────────────────────────────────────────────

import {
  PerformanceAlertItem,
  PerformanceAlertsResponse,
  PlatformPerformanceResponse,
  PlatformSummaryData,
} from './automationInsightsService';

function mockWindow(): { current_start: string; current_end: string; baseline_start: string; baseline_end: string } {
  return {
    current_start: '2026-08-10',
    current_end: '2026-08-16',
    baseline_start: '2026-07-13',
    baseline_end: '2026-08-09',
  };
}

const MOCK_IMPROVED: PerformanceAlertItem[] = [
  {
    automation_name: 'O&E INT OCEAN — Booking Amendment Bot',
    verdict: 'Improved',
    recent_volume: 412,
    baseline_volume: 1552,
    baseline_avg_weekly_volume: 388,
    recent_fulfilled_count: 377,
    baseline_fulfilled_count: 1322,
    delta_fulfillment: 6.2,
    recent_fulfillment: 91.4,
    baseline_fulfillment: 85.2,
    delta_mttr_pct: -18.5,
    recent_mttr: 2.1,
    baseline_mttr: 2.6,
    recent_mttr_ticket_count: 412,
    baseline_mttr_ticket_count: 1552,
    reasons: ['Fulfillment rose 6.2pp (85.2% → 91.4%) on 412 tickets in the last 7 days vs a pooled baseline of 85.2% over 1552 tickets across 4 weeks.', 'MTTR dropped 19% (2.6h → 2.1h) based on 412 resolved tickets in the last 7 days vs 1552 in the baseline.'],
    window: mockWindow(),
  },
  {
    automation_name: 'CLIC — Document Auto-Classifier',
    verdict: 'Improved',
    recent_volume: 205,
    baseline_volume: 792,
    baseline_avg_weekly_volume: 198,
    recent_fulfilled_count: 182,
    baseline_fulfilled_count: 680,
    delta_fulfillment: 3.1,
    recent_fulfillment: 88.9,
    baseline_fulfillment: 85.8,
    delta_mttr_pct: -9.2,
    recent_mttr: 1.4,
    baseline_mttr: 1.6,
    recent_mttr_ticket_count: 205,
    baseline_mttr_ticket_count: 792,
    reasons: ['Fulfillment rose 3.1pp (85.8% → 88.9%) on 205 tickets in the last 7 days vs a pooled baseline of 85.8% over 792 tickets across 4 weeks.'],
    window: mockWindow(),
  },
  {
    automation_name: 'FAM — Invoice Reconciliation',
    verdict: 'Improved',
    recent_volume: 156,
    baseline_volume: 596,
    baseline_avg_weekly_volume: 149,
    recent_fulfilled_count: 124,
    baseline_fulfilled_count: 460,
    delta_fulfillment: 2.4,
    recent_fulfillment: 79.5,
    baseline_fulfillment: 77.1,
    delta_mttr_pct: -4.1,
    recent_mttr: 3.2,
    baseline_mttr: 3.4,
    recent_mttr_ticket_count: 156,
    baseline_mttr_ticket_count: 596,
    reasons: ['Fulfillment rose 2.4pp (77.1% → 79.5%) on 156 tickets in the last 7 days vs a pooled baseline of 77.1% over 596 tickets across 4 weeks.'],
    window: mockWindow(),
  },
];

const MOCK_DEGRADED: PerformanceAlertItem[] = [
  {
    automation_name: 'AIRLCL — Rate Lookup Bot',
    verdict: 'Degraded',
    recent_volume: 88,
    baseline_volume: 408,
    baseline_avg_weekly_volume: 102,
    recent_fulfilled_count: 60,
    baseline_fulfilled_count: 310,
    delta_fulfillment: -7.8,
    recent_fulfillment: 68.2,
    baseline_fulfillment: 76.0,
    delta_mttr_pct: 22.3,
    recent_mttr: 4.4,
    baseline_mttr: 3.6,
    recent_mttr_ticket_count: 88,
    baseline_mttr_ticket_count: 408,
    reasons: ['Fulfillment dropped 7.8pp (76.0% → 68.2%) on 88 tickets in the last 7 days vs a pooled baseline of 76.0% over 408 tickets across 4 weeks.', 'Upstream rate API returning intermittent 502s.'],
    window: mockWindow(),
  },
  {
    automation_name: 'L0 SD — Password Reset Flow',
    verdict: 'Degraded',
    recent_volume: 301,
    baseline_volume: 1180,
    baseline_avg_weekly_volume: 295,
    recent_fulfilled_count: 283,
    baseline_fulfilled_count: 1152,
    delta_fulfillment: -3.5,
    recent_fulfillment: 94.1,
    baseline_fulfillment: 97.6,
    delta_mttr_pct: 11.0,
    recent_mttr: 0.9,
    baseline_mttr: 0.8,
    recent_mttr_ticket_count: 301,
    baseline_mttr_ticket_count: 1180,
    reasons: ['Minor MFA prompt regression after vendor update.'],
    window: mockWindow(),
  },
];

const MOCK_MIXED: PerformanceAlertItem[] = [
  {
    automation_name: 'TBM — VM Provisioning Bot',
    verdict: 'Mixed',
    recent_volume: 64,
    baseline_volume: 244,
    baseline_avg_weekly_volume: 61,
    recent_fulfilled_count: 38,
    baseline_fulfilled_count: 174,
    delta_fulfillment: -13.0,
    recent_fulfillment: 59.4,
    baseline_fulfillment: 71.3,
    delta_mttr_pct: -28.4,
    recent_mttr: 2.6,
    baseline_mttr: 3.6,
    recent_mttr_ticket_count: 64,
    baseline_mttr_ticket_count: 244,
    reasons: ['Fulfillment dropped 13.0pp (71.3% → 59.4%) on 64 tickets in the last 7 days vs a pooled baseline of 71.3% over 244 tickets across 4 weeks.', 'MTTR dropped 28% (3.6h → 2.6h) based on 64 resolved tickets in the last 7 days vs 244 in the baseline.'],
    window: mockWindow(),
  },
];

const MOCK_INCONCLUSIVE: PerformanceAlertItem[] = [
  {
    automation_name: 'BEP_INT — Contract Renewal Notice',
    verdict: 'Inconclusive',
    recent_volume: 3,
    baseline_volume: 224,
    baseline_avg_weekly_volume: 56,
    recent_fulfilled_count: 2,
    baseline_fulfilled_count: 190,
    delta_fulfillment: null,
    recent_fulfillment: 66.7,
    baseline_fulfillment: 84.8,
    delta_mttr_pct: null,
    recent_mttr: 2.0,
    baseline_mttr: 3.1,
    recent_mttr_ticket_count: 2,
    baseline_mttr_ticket_count: 190,
    reasons: ['Only 3 ticket(s) in the last 7 days vs a 4-week baseline average of 56.0/week (224 total) — too little volume to call this a trend.'],
    window: mockWindow(),
  },
];

export const MOCK_PERFORMANCE_ALERTS: PerformanceAlertsResponse = {
  platform: 'all',
  ticket_type: 'RITM',
  date_from: '2026-05-25',
  date_to: '2026-08-17',
  baseline_weeks: 4,
  improved_count: MOCK_IMPROVED.length,
  degraded_count: MOCK_DEGRADED.length,
  mixed_count: MOCK_MIXED.length,
  inconclusive_count: MOCK_INCONCLUSIVE.length,
  improved: MOCK_IMPROVED,
  degraded: MOCK_DEGRADED,
  mixed: MOCK_MIXED,
  inconclusive: MOCK_INCONCLUSIVE,
  verdicts: [...MOCK_DEGRADED, ...MOCK_MIXED, ...MOCK_INCONCLUSIVE, ...MOCK_IMPROVED],
};

const WEEK_LABELS = ['2026-W23', '2026-W24', '2026-W25', '2026-W26', '2026-W27', '2026-W28', '2026-W29', '2026-W30'];

function buildWeeklyTrend(base: { created: number; resolved: number; pct: number; mttr: number; tori: number; cne: number; reopen: number }) {
  return WEEK_LABELS.map((week_start, i) => {
    const volCreated = Math.round(base.created + Math.sin(i) * 12 + i * 2);
    const cneCount = Math.max(0, Math.round(base.cne + Math.cos(i + 1) * 2));
    const reopenCount = Math.max(0, Math.round(base.reopen + Math.sin(i + 3) * 1.5));
    return {
      week_start,
      vol_created: volCreated,
      vol_resolved: Math.round(base.resolved + Math.sin(i + 1) * 10 + i * 2),
      auto_fulfilled_pct: Math.round((base.pct + Math.sin(i + 2) * 3) * 10) / 10,
      avg_mttr: Math.round((base.mttr + Math.cos(i) * 0.4) * 10) / 10,
      tori_vol: Math.round(base.tori + Math.sin(i) * 4),
      cne_count: cneCount,
      // % of vol_created — same convention as auto_fulfilled_pct.
      cne_pct: volCreated ? Math.round((cneCount / volCreated) * 1000) / 10 : 0,
      reopen_count: reopenCount,
      reopen_pct: volCreated ? Math.round((reopenCount / volCreated) * 1000) / 10 : 0,
    };
  });
}

export const MOCK_PLATFORM_PERFORMANCE: PlatformPerformanceResponse = {
  platform: 'all',
  ticket_type: 'INC',
  date_from: '2026-06-22',
  date_to: '2026-08-17',
  basis: 'created',
  source_filters: {
    all: {
      kpis: {
        vol_created: 1842,
        vol_resolved: 1765,
        auto_fulfilled: 1103,
        auto_fulfilled_pct: 62.5,
        tori_vol: 214,
        cne_count: 38,
        cne_pct: 2.1,
        reopen_count: 52,
        reopen_pct: 2.8,
        avg_mttr: 3.4,
        avg_mttr_p50: 2.1,
        avg_mttr_p80: 5.6,
        avg_mttr_p85: 6.9,
        avg_mttr_p90: 9.2,
      },
      weekly_trend: buildWeeklyTrend({ created: 230, resolved: 220, pct: 62, mttr: 3.4, tori: 27, cne: 5, reopen: 6 }),
    },
    automation: {
      kpis: {
        vol_created: 1103,
        vol_resolved: 1080,
        auto_fulfilled: 1103,
        auto_fulfilled_pct: 100,
        tori_vol: 0,
        cne_count: 12,
        cne_pct: 1.1,
        reopen_count: 18,
        reopen_pct: 1.6,
        avg_mttr: 1.8,
        avg_mttr_p50: 1.2,
        avg_mttr_p80: 2.9,
        avg_mttr_p85: 3.4,
        avg_mttr_p90: 4.6,
      },
      weekly_trend: buildWeeklyTrend({ created: 138, resolved: 135, pct: 100, mttr: 1.8, tori: 0, cne: 2, reopen: 2 }),
    },
    tori: {
      kpis: {
        vol_created: 214,
        vol_resolved: 198,
        auto_fulfilled: 0,
        auto_fulfilled_pct: 0,
        tori_vol: 214,
        cne_count: 4,
        cne_pct: 1.9,
        reopen_count: 6,
        reopen_pct: 2.8,
        avg_mttr: 5.1,
        avg_mttr_p50: 3.8,
        avg_mttr_p80: 8.2,
        avg_mttr_p85: 9.5,
        avg_mttr_p90: 11.8,
      },
      weekly_trend: buildWeeklyTrend({ created: 27, resolved: 25, pct: 0, mttr: 5.1, tori: 27, cne: 1, reopen: 1 }),
    },
    human: {
      kpis: {
        vol_created: 525,
        vol_resolved: 487,
        auto_fulfilled: 0,
        auto_fulfilled_pct: 0,
        tori_vol: 0,
        cne_count: 22,
        cne_pct: 4.2,
        reopen_count: 28,
        reopen_pct: 5.3,
        avg_mttr: 6.7,
        avg_mttr_p50: 4.9,
        avg_mttr_p80: 11.3,
        avg_mttr_p85: 13.1,
        avg_mttr_p90: 16.4,
      },
      weekly_trend: buildWeeklyTrend({ created: 65, resolved: 60, pct: 0, mttr: 6.7, tori: 0, cne: 3, reopen: 3 }),
    },
  },
};

const PLATFORM_ROW_INPUTS: Array<{ platform_key: string; platform_name: string; created: number; resolved: number; auto: number; tori: number; cne: number; reopen: number; open: number; mttr: number }> = [
  { platform_key: 'O&E_INT_OCEAN', platform_name: 'O&E INT OCEAN', created: 512, resolved: 498, auto: 340, tori: 62, cne: 8, reopen: 12, open: 21, mttr: 2.4 },
  { platform_key: 'O&E_LNS', platform_name: 'O&E LNS', created: 298, resolved: 285, auto: 190, tori: 34, cne: 5, reopen: 7, open: 14, mttr: 3.1 },
  { platform_key: 'CLIC', platform_name: 'CLIC', created: 205, resolved: 198, auto: 145, tori: 18, cne: 3, reopen: 4, open: 9, mttr: 1.9 },
  { platform_key: 'FAM', platform_name: 'FAM', created: 156, resolved: 149, auto: 88, tori: 22, cne: 4, reopen: 6, open: 8, mttr: 3.6 },
  { platform_key: 'AIRLCL', platform_name: 'AIRLCL', created: 88, resolved: 79, auto: 41, tori: 9, cne: 6, reopen: 5, open: 11, mttr: 4.2 },
  { platform_key: 'L0 SD', platform_name: 'L0 SD', created: 301, resolved: 296, auto: 268, tori: 12, cne: 2, reopen: 3, open: 6, mttr: 0.9 },
];

function toSummaryRow(input: typeof PLATFORM_ROW_INPUTS[number], ticketType: 'ALL' | 'INC' | 'RITM') {
  return {
    platform_key: input.platform_key,
    platform_name: input.platform_name,
    ticket_type: ticketType,
    vol_created: input.created,
    vol_resolved: input.resolved,
    auto_fulfilled: input.auto,
    tori_vol: input.tori,
    cne_count: input.cne,
    reopen_count: input.reopen,
    open_tickets: input.open,
    avg_mttr: input.mttr,
    avg_mttr_p50: Math.round(input.mttr * 0.6 * 10) / 10,
    avg_mttr_p80: Math.round(input.mttr * 1.7 * 10) / 10,
    avg_mttr_p85: Math.round(input.mttr * 2.0 * 10) / 10,
    avg_mttr_p90: Math.round(input.mttr * 2.6 * 10) / 10,
  };
}

export function buildMockPlatformSummary(ticketType: 'ALL' | 'INC' | 'RITM'): PlatformSummaryData {
  const rows = PLATFORM_ROW_INPUTS.map((input) => toSummaryRow(input, ticketType));

  const grand_total = rows.reduce(
    (acc, row) => ({
      vol_created: acc.vol_created + row.vol_created,
      vol_resolved: acc.vol_resolved + row.vol_resolved,
      auto_fulfilled: acc.auto_fulfilled + row.auto_fulfilled,
      tori_vol: acc.tori_vol + row.tori_vol,
      cne_count: acc.cne_count + row.cne_count,
      reopen_count: acc.reopen_count + row.reopen_count,
      open_tickets: acc.open_tickets + row.open_tickets,
      avg_mttr: acc.avg_mttr, // averaged below
      avg_mttr_p50: acc.avg_mttr_p50,
      avg_mttr_p80: acc.avg_mttr_p80,
      avg_mttr_p85: acc.avg_mttr_p85,
      avg_mttr_p90: acc.avg_mttr_p90,
    }),
    { vol_created: 0, vol_resolved: 0, auto_fulfilled: 0, tori_vol: 0, cne_count: 0, reopen_count: 0, open_tickets: 0, avg_mttr: 0, avg_mttr_p50: 0, avg_mttr_p80: 0, avg_mttr_p85: 0, avg_mttr_p90: 0 }
  );
  const avgMttr = rows.reduce((sum, r) => sum + r.avg_mttr, 0) / rows.length;
  grand_total.avg_mttr = Math.round(avgMttr * 10) / 10;

  return {
    ticket_type: ticketType,
    source_filter: 'all',
    date_from: '2026-05-25',
    date_to: '2026-08-17',
    basis: 'created',
    rows,
    grand_total,
  };
}

export const MOCK_PLATFORM_SUMMARY: PlatformSummaryData = buildMockPlatformSummary('ALL');
